import { newAnnotationLinkId } from './AnnotationLinkId';
import type { AnnotationLinkOrigin } from './AnnotationLinkOrigin';
import type {
  AnnotationLinkFromKind,
  AnnotationLinkId,
  AnnotationLinkJSON,
  AnnotationLinkStyle,
  ModelId,
  ViewModelId,
} from 'balus-common';
import { RTDBPath } from 'balus-common';
import type { LinkableTargetKey } from '@view-model/domain/key';
import { buildLinkableTargetKey } from '@view-model/models/sticky/StickyLink/domain/LinkEntity';
import { LinkBend } from '@view-model/models/sticky/StickyLink/domain/vo/LinkBend';
import { ObjectRepository } from '@framework/repository';
import type { ICommand } from '@model-framework/command';
import { CreateCommand } from '@model-framework/command';

export class AnnotationLink {
  constructor(
    public readonly id: AnnotationLinkId,
    public readonly fromKind: AnnotationLinkFromKind,
    public readonly fromId: AnnotationLinkOrigin['id'],
    public readonly to: LinkableTargetKey,
    public readonly style: AnnotationLinkStyle,
    public readonly reversed: boolean,
    public readonly bend: LinkBend
  ) {}

  static load(dump: AnnotationLinkJSON): AnnotationLink {
    const { id, fromKind, fromId, toKey, style, reversed, bend } = dump;
    return new AnnotationLink(
      id,
      fromKind,
      fromId,
      buildLinkableTargetKey(toKey),
      style,
      reversed ?? false,
      bend ? LinkBend.load(bend) : LinkBend.Straight()
    );
  }

  dump(): AnnotationLinkJSON {
    const { id, fromKind, fromId, to, style, reversed, bend } = this;
    return { id, fromKind, fromId, toKey: to.toString(), style, reversed, bend: bend.dump() };
  }

  static buildNew(from: AnnotationLinkOrigin, to: LinkableTargetKey): AnnotationLink {
    return new AnnotationLink(newAnnotationLinkId(), from.kind, from.id, to, 'wedge', false, LinkBend.Straight());
  }

  withStyle(style: AnnotationLinkStyle): AnnotationLink {
    return new AnnotationLink(this.id, this.fromKind, this.fromId, this.to, style, this.reversed, this.bend);
  }

  reverse(): AnnotationLink {
    return new AnnotationLink(this.id, this.fromKind, this.fromId, this.to, this.style, !this.reversed, this.bend);
  }

  withBend(bend: LinkBend): AnnotationLink {
    return new AnnotationLink(this.id, this.fromKind, this.fromId, this.to, this.style, this.reversed, bend);
  }

  cloneNew(
    fromIdMap: Record<AnnotationLinkOrigin['id'], AnnotationLinkOrigin['id']>,
    targetKeyMap: Record<string, LinkableTargetKey>
  ): AnnotationLink {
    return new AnnotationLink(
      newAnnotationLinkId(),
      this.fromKind,
      fromIdMap[this.fromId],
      targetKeyMap[this.to.toString()],
      this.style,
      this.reversed,
      this.bend
    );
  }

  isEqual(other: AnnotationLink): boolean {
    return (
      this.id === other.id &&
      this.fromKind === other.fromKind &&
      this.fromId === other.fromId &&
      this.to.isEqual(other.to) &&
      this.style === other.style &&
      this.reversed === other.reversed &&
      this.bend.isEquals(other.bend)
    );
  }

  buildCreateCommand(viewModelId: ViewModelId, modelId: ModelId): ICommand {
    const repository = new ObjectRepository(
      AnnotationLink,
      RTDBPath.AnnotationLink.linkPath(viewModelId, modelId, this.id)
    );
    return new CreateCommand(this, repository);
  }
}
